import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { ConfirmComponent } from 'src/app/components/shared/components/confirm/confirm.component';
import { SuccessComponent } from 'src/app/components/shared/components/success/success.component';
import { UserService } from 'src/app/core/services/user.service';
import { UnitsService } from 'src/app/core/services/units.service';
import { ICase } from '../../interfaces/case.interface';
import { CasesService } from '../../../../core/services/cases.service';
import { CreateCaseComponent } from '../create-case/create-case.component';
import { FinishCaseComponent } from '../finishCase/finishCase.component';
import { RemitCaseComponent } from '../remit-case/remit-case.component';
import { RemitirCsaComponent } from '../remitir-csa/remitir-csa.component';
import { RasNumberComponent } from 'src/app/components/shared/components/ras-number/ras-number.component';
import { CreateBtCaseComponent } from '../create-bt-case/create-bt-case.component';
import { TransferCaseService } from 'src/app/services/transfer_case_service.service';

@Component({
  selector: 'case-card',
  templateUrl: './case-card.component.html',
  styleUrls: ['./case-card.component.scss'],
})
export class CaseCardComponent implements OnInit {
  @Input() case!: ICase;
  canRemitCSA: boolean = false;

  constructor(
    private readonly caseService: CasesService,
    private readonly dialog: MatDialog,
    public router: Router,
    private readonly route: ActivatedRoute,
    public userService: UserService,
    private readonly unitsService: UnitsService,
    private readonly transferCaseService: TransferCaseService
  ) {}

  /**
   * El botón de devolver caso debe mostrarse solo cuando la unidad del caso sea CAE o CAI
   * y el usuario tenga rol 'Coordinador Asuntos Externos' o 'Coordinador Asuntos Internos'
   */
  get canReturnCase() {
    try {
      if (!this.case) return false;
      const unitCode = this.case.caseType?.unit?.code;
      const roleName = this.userService.currentUser?.role?.name;
      const allowedUnits = ['CAE', 'CAI'];
      const allowedRoles = [
        'Coordinador Asuntos Externos',
        'Coordinador Asuntos Internos',
      ];
      return allowedUnits.includes(unitCode) && allowedRoles.includes(roleName);
    } catch {
      return false;
    }
  }

  async confirmReturnCase() {
    // Abrir diálogo que solicita motivo y devuelve {confirmed, reason} o false
    const dialogRef = this.dialog.open(ConfirmComponent, {
      data: {
        title: "¿Deseas devolver/rechazar este caso a 'casos sin asignar'?",
        showReasonField: true,
      },
      minWidth: '400px',
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result === false || !result.confirmed) return;
      const payload = {
        userId: this.userService.currentUser?._id,
        reason: result.reason || 'Devuelto desde tarjeta de caso por gerente',
        restoreOriginal: true,
      };
      this.transferCaseService.returnCase(this.case._id, payload).subscribe({
        next: () => {
          this.alert('El caso ha sido devuelto correctamente');
          try {
            this.caseService.filterData.next({});
          } catch (e) {
            console.debug('Could not trigger cases reload', e);
          }
        },
        error: (e) => {
          console.error(e);
          this.alert('Ocurrió un error al intentar devolver el caso');
        },
      });
    });
  }

  get canOpen() {
    /* Incidencias no debe poder abrir casos */
    if (!this.case || !this.case.gai) return false;
    const isIncidencia = this.case.gai.indexOf('I-') == 0 ? true : false;
    return !isIncidencia;
  }

  get canEdit() {
    const permission = this.userService.permissions?.editCases;
    if (!permission) return false;
    if (!this.case) return false;
    if (
      this.userService.permissions?.flagHelper &&
      this.case.additionalInformation?.allParams
    ) {
      return false;
    }
    /* Incidencias no debe poder editar casos */
    const isIncidencia = this.case.gai?.indexOf('I-') == 0 ? true : false;
    return permission && !isIncidencia;
  }

  get canDelete() {
    return this.userService.permissions?.deleteCases;
  }

  get canFinish() {
    return this.userService.permissions?.finishCases;
  }

  async ngOnInitAsync() {
    // kept for compatibility if needed
  }

  ngOnInit(): void {
    try {
      const unitId =
        this.route.snapshot.paramMap.get('unit_id') ||
        this.route.snapshot.paramMap.get('id');
      // if no unit id in route, keep false
      if (!unitId) return;
      // fetch unit and check if code === 'CSA' and user role is Gerente de Línea
      this.unitsService.getById(unitId).subscribe({
        next: (unit: any) => {
          const roleName = this.userService.currentUser?.role?.name;
          this.canRemitCSA = roleName === 'Gerente' && unit?.code === 'CSA';
        },
        error: () => (this.canRemitCSA = false),
      });
    } catch (e) {
      this.canRemitCSA = false;
    }
  }

  get requireSecondAp() {
    if (!this.case) return false;
    return (
      this.case.caseType?.approvalRoles?.levelTwo &&
      this.case.approvalStatus == 'requestLevelTwo'
    );
  }

  get requireLevelThree() {
    if (!this.case) return false;
    return (
      this.case.caseType?.approvalRoles?.levelThree &&
      this.case.approvalStatus == 'requestLevelThree'
    );
  }
  get requireFirstdAp() {
    if (!this.case) return false;
    return (
      this.case.caseType?.approvalRoles?.levelOne &&
      this.case.approvalStatus == 'requestLevelOne'
    );
  }

  get controlRas() {
    if (!this.case) return false;
    if (
      this.case.caseType?.approvalRoles?.levelTwo &&
      this.case.approvalStatus == 'approved'
    ) {
      return this.case?.numberRas ? true : false;
    }
    return false;
  }

  get secondAp() {
    if (!this.case) return false;
    return this.case.caseType?.approvalRoles?.levelTwo ? true : false;
  }
  get threeAp() {
    if (!this.case) return false;
    return this.case.caseType?.approvalRoles?.levelThree ? true : false;
  }
  get appUnif() {
    if (!this.case) return false;
    if (
      this.case.caseType?.approvalRoles?.levelTwo &&
      this.case.approvalStatus == 'approved'
    ) {
      return this.case?.apUnif;
    }
    return false;
  }

  get canControlRas() {
    try {
      if (!this.case) return false;
      const { approvalRoles } = this.case.caseType || {};
      const { approvalStatus } = this.case;
      const roleId = this.userService.currentUser?.role?._id;
      if (
        approvalRoles?.levelOne == roleId &&
        approvalStatus == 'approved' &&
        this.case.caseType?.name == 'RAS'
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
  get canApprovedUnif() {
    try {
      if (!this.case) return false;
      const { approvalRoles } = this.case.caseType || {};
      const { approvalStatus } = this.case;
      const roleId = this.userService.currentUser?.role?._id;
      if (approvalRoles?.levelTwo == roleId && approvalStatus == 'approved') {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
  get additionalInformation() {
    return this.case?.additionalInformation;
  }

  get additionalInformationFechaDelReporte() {
    return this.case?.additionalInformation?.fecha_del_reporte;
  }

  get additionalInformationNumeroDeIdentificacion() {
    return this.case?.additionalInformation?.numero_de_identificacion;
  }

  get additionalInformationNivelDeRiesgo() {
    return this.case?.additionalInformation?.nivel_de_riesgo;
  }

  get aproved() {
    return this.case?.approvalStatus == 'approved';
  }

  get assignedTo() {
    return this.case?.assigned?.fullname || 'Requiere usuario *';
  }

  get caseType() {
    return this.case?.caseType?.name;
  }

  get allDataFalse() {
    if (!this.case) return false;
    return (
      this.case.caseType?.unit?.code == 'CSA' &&
      !this.case.additionalInformation?.allParams
    );
  }

  finish() {
    // if (!this.appUnif && this.secondAp) {
    //   return this.alert('El caso no ha sido aprobado por UNIF');
    // }
    // if (!this.controlRas && this.secondAp && this.threeAp) {
    //   return this.alert('El caso no posee Número de control RAS');
    // }
    // if (!this.threeAp) {
    //   return this.alert('El caso no posee todas las probaciones');
    // }
    this.dialog.open(FinishCaseComponent, {
      width: '800px',
      maxHeight: '80vh',
      data: this.case,
    });
  }

  remitTo() {
    const caseType: any = this.case.caseType;

    if (!caseType.remitTo || caseType.remitTo.length === 0) {
      return this.finish();
    }

    const finishedDestinations = caseType.remitTo.filter(
      (instruction) => instruction.remitIf === 'finished'
    );

    if (finishedDestinations.length > 0) {
      this.dialog
        .open(RemitCaseComponent, {
          width: '600px',
          maxHeight: '80vh',
          data: {
            case: this.case,
            destinations: finishedDestinations,
          },
          disableClose: true,
        })
        .afterClosed()
        .subscribe({
          next: (response) => {
            this.finish();
          },
        });
    } else {
      this.finish();
    }
  }
  edit() {
    if (this.case.caseType.unit.code == 'CSA') {
      this.dialog
        .open(CreateBtCaseComponent, {
          width: '90%',
          data: {
            unit: this.case.caseType.unit,
            case: this.case,
            edit: true,
          },
          disableClose: true,
        })
        .afterClosed()
        .subscribe({
          next: (caseUpdated: ICase) => {
            if (caseUpdated) {
              this.case = caseUpdated;
              this.caseService.caseEdited.next(this.case);
            }
          },
        });
    } else {
      let unitId =
        typeof this.case.caseType.unit == 'string'
          ? this.case.caseType.unit
          : this.case.caseType.unit._id;

      this.dialog
        .open(CreateCaseComponent, {
          width: '700px',
          maxHeight: '80vh',
          data: {
            unit: unitId,
            case: this.case,
          },
          disableClose: true,
        })
        .afterClosed()
        .subscribe({
          next: (caseUpdated: ICase) => {
            if (caseUpdated) {
              this.case = caseUpdated;
              this.caseService.caseEdited.next(this.case);
            }
          },
        });
    }
  }

  confirm(message) {
    return new Promise((resolve) => {
      this.dialog
        .open(ConfirmComponent, {
          data: {
            title: message,
          },
        })
        .afterClosed()
        .subscribe({ next: (result) => resolve(result) });
    });
  }

  alert(message) {
    this.dialog.open(SuccessComponent, { data: { title: message } });
  }

  async delete() {
    let response = await this.confirm('¿Seguro deseas eliminar este caso?');
    if (response) {
      this.caseService
        .delete(this.case._id, this.userService.currentUser)
        .subscribe({
          next: () => {
            this.caseService.caseDeleted.next(this.case);
            this.alert('El caso ha sido eliminado');
          },
          error: (e) => {
            console.log(e);
            this.alert('Ha ocurrido un error al intentar eliminar el caso');
          },
        });
    }
  }

  goToCase() {
    this.router.navigate([`case/${this.case._id}`]);
  }

  async changeUnif() {
    this.dialog
      .open(ConfirmComponent, {
        data: {
          title: '¿Caso aprobado por UNIF?',
        },
        minWidth: '400px',
      })
      .afterClosed()
      .subscribe(async (re) => {
        await this.caseService
          .update(this.case._id, {
            apUnif: re,
          })
          .subscribe((caseUpdated) => {
            this.case = caseUpdated;
            this.caseService.caseEdited.next(this.case);
          });
      });
  }

  async addNumberRas() {
    this.dialog
      .open(RasNumberComponent, {
        minWidth: '400px',
      })
      .afterClosed()
      .subscribe(async (re) => {
        if (re.status) {
          await this.caseService
            .update(this.case._id, {
              numberRas: re.value.num,
              desRas: re.value.observation,
            })
            .subscribe((caseUpdated) => {
              this.case = caseUpdated;
              this.caseService.caseEdited.next(this.case);
            });
        }
      });
  }

  remitirCSA() {
    // Solo Gerente de Línea puede usarlo (template controla visibilidad)
    this.dialog
      .open(RemitirCsaComponent, {
        width: '600px',
        maxHeight: '80vh',
        data: this.case,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((updatedCase) => {
        if (updatedCase) {
          this.case = updatedCase;
          this.caseService.caseEdited.next(this.case);
          // Trigger a reload of cases in the parent lists so updated/removed cases are fetched
          try {
            this.caseService.filterData.next({});
          } catch (e) {
            console.debug('Could not trigger cases reload', e);
          }
        }
      });
  }
}
